(() => {
  const roots = document.querySelectorAll('[data-worker-search]');

  if (!roots.length || !('fetch' in window)) return;

  const productImageSizes = '(min-width: 1250px) 295px, (min-width: 990px) calc((100vw - 5rem) / 4), (min-width: 750px) calc((100vw - 4rem) / 3), calc((100vw - 3rem) / 2)';
  const minimumQueryLength = 2;
  const trendingWindow = '12h';
  const infiniteScrollPreloadMargin = 900;
  const infiniteScrollRootMargin = `${infiniteScrollPreloadMargin}px 0px`;

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const safeJson = (value) => {
    return JSON.stringify(value).replace(/</g, '\\u003c');
  };

  const normalizeEndpoint = (endpoint) => {
    return String(endpoint || '').trim().replace(/\/+$/, '');
  };

  const getRequestId = () => {
    return window.crypto?.randomUUID?.() || `request-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const getWorkerSessionId = () => {
    return window.WobWorkerAnalytics?.getSessionId?.() || '';
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';

    return String(url).startsWith('//') ? `${window.location.protocol}${url}` : String(url);
  };

  const withImageWidth = (url, width) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (!normalizedUrl) return '';

    try {
      const parsedUrl = new URL(normalizedUrl, window.location.origin);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (error) {
      const separator = normalizedUrl.includes('?') ? '&' : '?';

      return `${normalizedUrl}${separator}width=${width}`;
    }
  };

  const buildSrcset = (url) => {
    return [320, 540, 760]
      .map((width) => `${withImageWidth(url, width)} ${width}w`)
      .join(', ');
  };

  const moneyAmount = (money) => {
    const amount = Number(money?.amount ?? money ?? 0);

    return Number.isFinite(amount) ? amount : 0;
  };

  const formatMoney = (amount, currencyCode = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const handleize = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const getNumericId = (value) => {
    const id = typeof value === 'object' ? (value?.numericId || value?.id) : value;
    const match = String(id || '').match(/(\d+)$/);

    return match ? match[1] : String(id || '');
  };

  const isHiddenVariant = (variant) => {
    return Boolean(
      variant?.hidden
        || variant?.hideVariant
        || variant?.hide_from_search
        || variant?.metafields?.custom?.hide_variant
    );
  };

  const isHiddenProduct = (product) => {
    return Boolean(
      product?.hidden
        || product?.hideFromSearch
        || product?.hide_from_search
        || product?.metafields?.custom?.hide_from_search
    );
  };

  const getProductUrl = (product) => {
    if (product?.url) return product.url;
    if (product?.handle) return `/products/${encodeURIComponent(product.handle)}`;

    return '#';
  };

  const getSearchProductUrl = (root, url = '', productHandle = '') => {
    const fallbackUrl = url || (productHandle ? `/products/${productHandle}` : '');

    if (!fallbackUrl) return '#';

    try {
      const parsedUrl = new URL(fallbackUrl, window.location.origin);

      return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch (error) {
      return fallbackUrl;
    }
  };

  const getCollectionUrl = (collection) => {
    if (collection?.url) return collection.url;
    if (collection?.handle) return `/collections/${encodeURIComponent(collection.handle)}`;

    return '#';
  };

  const getVisibleVariants = (product) => {
    return (product?.variants || []).filter((variant) => !isHiddenVariant(variant));
  };

  const isVariantAvailable = (variant) => {
    return Boolean(variant?.availableForSale || variant?.available);
  };

  const getSelectedVariant = (product) => {
    const variants = getVisibleVariants(product);
    const selectedVariantId = String(product?.selectedVariantId || '');

    return variants.find((variant) => String(variant.id) === selectedVariantId)
      || variants.find((variant) => getNumericId(variant) === selectedVariantId)
      || variants.find(isVariantAvailable)
      || variants[0]
      || null;
  };

  const getOptionName = (option) => {
    return typeof option === 'string' ? option : option?.name || '';
  };

  const findOptionIndex = (options, matcher) => {
    if (!Array.isArray(options)) return -1;

    return options.findIndex((option) => matcher(getOptionName(option).toLowerCase()));
  };

  const getVariantOptionValue = (variant, optionName) => {
    return (variant?.selectedOptions || []).find((option) => option.name === optionName)?.value || '';
  };

  const getVariantOptions = (variant) => {
    if (Array.isArray(variant?.options)) return variant.options;

    return (variant?.selectedOptions || []).map((option) => option.value).filter(Boolean);
  };

  const getIcon = (root, icon) => {
    return root.querySelector(`template[data-search-worker-icon="${icon}"]`)?.innerHTML.trim() || '';
  };

  const normalizeSearchProduct = (product) => {
    if (!product || isHiddenProduct(product)) return null;

    const originalVariants = Array.isArray(product.variants) ? product.variants : [];
    const visibleVariants = originalVariants.filter((variant) => !isHiddenVariant(variant));

    if (originalVariants.length && !visibleVariants.length) return null;

    const visibleVariantKeys = new Set(
      visibleVariants.flatMap((variant) => [String(variant.id || ''), String(getNumericId(variant))]).filter(Boolean)
    );
    const explicitVariantId = product.cardVariantId || product.variantId || product.selectedVariantId;
    const isVariantCard = Boolean(
      product.cardVariantId
        || product.variantId
        || product.variantWeight
        || product.cardType === 'variant'
        || product.resultType === 'variant'
    );

    if (isVariantCard && explicitVariantId && !visibleVariantKeys.has(String(explicitVariantId)) && !visibleVariantKeys.has(String(getNumericId(explicitVariantId)))) {
      return null;
    }

    const options = Array.isArray(product.options) ? product.options : [];
    const colorOptionIndex = product.colorOptionIndex !== undefined && product.colorOptionIndex !== null && Number.isFinite(Number(product.colorOptionIndex))
      ? Number(product.colorOptionIndex)
      : findOptionIndex(options, (name) => name.includes('color') || name.includes('colour'));
    const sizeOptionIndex = product.sizeOptionIndex !== undefined && product.sizeOptionIndex !== null && Number.isFinite(Number(product.sizeOptionIndex))
      ? Number(product.sizeOptionIndex)
      : findOptionIndex(options, (name) => name.includes('size'));
    const selectedVariant = visibleVariants.find((variant) => {
      return String(variant.id) === String(explicitVariantId) || String(getNumericId(variant)) === String(getNumericId(explicitVariantId));
    }) || visibleVariants.find(isVariantAvailable) || visibleVariants[0] || null;
    const swatches = (product.swatches || []).filter((swatch) => {
      if (swatch?.hidden || swatch?.hideVariant) return false;
      if (!swatch?.variantId) return true;

      return visibleVariantKeys.has(String(swatch.variantId)) || visibleVariantKeys.has(String(getNumericId(swatch.variantId)));
    });
    const availableForSale = originalVariants.length
      ? visibleVariants.some(isVariantAvailable)
      : Boolean(product.availableForSale);

    return {
      ...product,
      availableForSale,
      colorOptionIndex,
      selectedVariantId: selectedVariant?.id || product.selectedVariantId || '',
      sizeOptionIndex,
      swatches,
      variants: visibleVariants
    };
  };

  const renderCardImage = (image, title, { className = '', eager = false } = {}) => {
    if (!image?.url) return '';

    const src = withImageWidth(image.url, 540);
    const width = image.width || 540;
    const height = image.height || 810;

    return `
      <img
        ${className ? `class="${escapeHtml(className)}"` : ''}
        data-product-image
        ${className ? '' : 'data-product-card-primary-image'}
        src="${escapeHtml(src)}"
        srcset="${escapeHtml(buildSrcset(image.url))}"
        sizes="${productImageSizes}"
        width="${width}"
        height="${height}"
        alt="${escapeHtml(image.altText || title)}"
        loading="${eager ? 'eager' : 'lazy'}"
        ${eager ? 'fetchpriority="high"' : ''}
        decoding="async"
      >
    `;
  };

  const renderPrice = (root, product) => {
    const currencyCode = product?.priceRange?.minVariantPrice?.currencyCode || root.dataset.currencyCode || 'USD';
    const price = moneyAmount(product?.priceRange?.minVariantPrice ?? product?.price);
    const maxPrice = moneyAmount(product?.priceRange?.maxVariantPrice ?? product?.price);
    const compareAtPrice = moneyAmount(product?.compareAtPriceRange?.maxVariantPrice ?? product?.compareAtPrice);
    const showFrom = maxPrice > price;
    const onSale = compareAtPrice > price;
    const soldOut = !product?.availableForSale;
    const selectedVariant = (product?.variants || []).find((variant) => moneyAmount(variant?.price) === price)
      || getSelectedVariant(product);
    const productId = product?.numericId || product?.id || '';
    const variantId = getNumericId(selectedVariant);

    return `
      <div class="price${onSale ? ' price--on-sale' : ''}${soldOut ? ' price--sold-out' : ''}" data-price data-product-price data-product-id="${escapeHtml(productId)}" data-variant-id="${escapeHtml(variantId)}" data-base-price-cents="${Math.round(price * 100)}" data-compare-at-price-cents="${Math.round(compareAtPrice * 100)}" data-price-show-from="${showFrom}">
        ${onSale ? `
          <span class="visually-hidden" data-price-compare-label>Regular price</span>
          <s class="price__compare" data-price-compare>${formatMoney(compareAtPrice, currencyCode)}</s>
        ` : ''}
        <span class="visually-hidden" data-price-current-label>${onSale ? 'Sale price' : 'Regular price'}</span>
        <span class="price__current" data-price-current>
          ${showFrom ? '<span class="price__from">From</span>' : ''}
          ${formatMoney(price, currencyCode)}
        </span>
      </div>
    `;
  };

  const renderBadges = (product) => {
    const tagLabel = (product?.tags || []).find((tag) => String(tag).startsWith('_label_'));
    const existingLabel = product?.badges?.customLabel || (tagLabel ? String(tagLabel).replace('_label_', '') : '');
    const tags = (product?.tags || []).map((tag) => String(tag).trim().toLowerCase());
    const isBestseller = tags.includes('bestseller') || tags.includes('_label_bestseller') || String(existingLabel).trim().toLowerCase() === 'bestseller';
    let customLabel = existingLabel;
    if (isBestseller) {
      customLabel = 'BESTSELLER';
    } else if (tags.includes('new to sale')) {
      customLabel = 'NEW TO SALE';
    } else if (tags.includes('wob exclusive')) {
      customLabel = 'ONLY AT WO';
    }
    const compareAtPrice = moneyAmount(product?.compareAtPriceRange?.maxVariantPrice ?? product?.compareAtPrice);
    const price = moneyAmount(product?.priceRange?.minVariantPrice ?? product?.price);
    const onSale = product?.badges?.onSale || compareAtPrice > price;

    if (customLabel) {
      return `<span class="product-card__badge product-card__badge--custom">${escapeHtml(customLabel)}</span>`;
    }

    if (product?.badges?.soldOut || !product?.availableForSale) {
      return '<span class="product-card__badge">Sold out</span>';
    }

    if (onSale) {
      const salePercent = product?.badges?.salePercent || Math.round(((compareAtPrice - price) / compareAtPrice) * 100) || 0;

      return `<span class="product-card__badge product-card__badge--sale">${escapeHtml(salePercent)}% off</span>`;
    }

    return '';
  };

  const formatSizeLabel = (value) => {
    const normalized = String(value || '').trim().toLowerCase();

    if (normalized === 'small') return 'S';
    if (normalized === 'medium') return 'M';
    if (normalized === 'large') return 'L';

    return value;
  };

  const findVariantForSize = ({ colorOptionName, selectedColor, sizeOptionName, sizeValue, variants }) => {
    const normalizedSize = String(sizeValue || '').toLowerCase();
    const normalizedColor = String(selectedColor || '').toLowerCase();

    return variants.find((variant) => {
      if (variant.hidden) return false;
      if (String(getVariantOptionValue(variant, sizeOptionName)).toLowerCase() !== normalizedSize) return false;
      if (!colorOptionName || !normalizedColor) return true;

      return String(getVariantOptionValue(variant, colorOptionName)).toLowerCase() === normalizedColor;
    }) || null;
  };

  const renderQuickAdd = (root, product) => {
    const variants = getVisibleVariants(product);
    const selectedVariant = getSelectedVariant(product);
    const plusIcon = getIcon(root, 'plus');
    const productTitle = product?.title || 'Product';

    if (!product?.availableForSale || !selectedVariant) {
      return '<span class="product-card__quick-unavailable">Sold out</span>';
    }

    const options = product?.options || [];
    const colorOptionName = product.colorOptionIndex > -1 ? getOptionName(options[product.colorOptionIndex]) : '';
    const sizeOptionName = product.sizeOptionIndex > -1 ? getOptionName(options[product.sizeOptionIndex]) : '';
    const selectedColor = product.selectedColor || getVariantOptionValue(selectedVariant, colorOptionName);
    const sizes = Array.from(new Set(variants.map((variant) => getVariantOptionValue(variant, sizeOptionName)).filter(Boolean)));

    if (sizeOptionName && sizes.length) {
      const sizeForms = sizes.map((size) => {
        const variant = findVariantForSize({
          colorOptionName,
          selectedColor,
          sizeOptionName,
          sizeValue: size,
          variants
        });
        const available = isVariantAvailable(variant);
        const label = formatSizeLabel(size);

        return `
          <form
            class="product-card__size-form"
            method="post"
            action="/cart/add"
            data-cart-add-form
            data-product-card-size-form
            data-product-card-size-value="${escapeHtml(size)}"
            data-product-card-size-display="${escapeHtml(label)}"
          >
            <input type="hidden" name="id" value="${escapeHtml(getNumericId(variant))}" data-product-card-variant-input>
            <input type="hidden" name="quantity" value="1">
            <button
              class="product-card__size-button"
              type="submit"
              data-product-card-size-button
              ${available ? '' : 'disabled'}
              aria-label="${escapeHtml(available ? `Add ${productTitle} - ${size} to bag` : `${productTitle} - ${size} unavailable`)}"
            >
              ${escapeHtml(label)}
            </button>
          </form>
        `;
      }).join('');

      return `
        <details class="product-card__quick-add">
          <summary>
            ${plusIcon}
            <span>Quick add</span>
          </summary>
          <div class="product-card__quick-options" aria-label="Quick add ${escapeHtml(productTitle)}">
            ${sizeForms}
          </div>
        </details>
      `;
    }

    return `
      <form class="product-card__quick-form" method="post" action="/cart/add" data-cart-add-form>
        <input type="hidden" name="id" value="${escapeHtml(getNumericId(selectedVariant))}" data-product-card-variant-input>
        <input type="hidden" name="quantity" value="1">
        <button class="product-card__quick-submit" type="submit" aria-label="Add ${escapeHtml(productTitle)} to bag">
          ${plusIcon}
          <span>Add to cart</span>
        </button>
      </form>
    `;
  };

  const renderSelectedColor = (product) => {
    const swatches = Array.isArray(product?.swatches) ? product.swatches : [];
    if (swatches.length <= 1) return '';

    const selectedVariant = getSelectedVariant(product);
    const options = product?.options || [];
    const colorOptionName = product.colorOptionIndex > -1 ? getOptionName(options[product.colorOptionIndex]) : '';
    const color = product?.selectedColor || getVariantOptionValue(selectedVariant, colorOptionName);

    if (!color) return '';

    return `
      <p class="product-card__selected-color" data-product-card-selected-color>
        Color: <span data-product-card-selected-color-text data-swatch-label-raw="${escapeHtml(color)}">${escapeHtml(window.WobSwatches?.getSwatchLabel?.(color) || color)}</span>
      </p>
    `;
  };

  const getSwatchStyle = (value) => {
    return window.WobSwatches?.getSwatchStyle?.(value) || '--swatch-color: #d9d9d9;';
  };

  const renderSwatches = (root, product) => {
    const swatches = window.WobSwatches?.getUniqueSwatches?.(product?.swatches) || [];
    if (swatches.length <= 1) return '';

    const visibleSwatchLimit = 6;
    const selectedVariant = getSelectedVariant(product);
    const options = product?.options || [];
    const colorOptionName = product.colorOptionIndex > -1 ? getOptionName(options[product.colorOptionIndex]) : '';
    const selectedColor = String(product.selectedColor || getVariantOptionValue(selectedVariant, colorOptionName) || '').toLowerCase();

    return `
      <div class="product-card__swatches" aria-label="Color options for ${escapeHtml(product.title)}">
        ${swatches.map((swatch, swatchIndex) => {
          const color = swatch.color || '';
          const variantId = getNumericId(swatch.variantId);
          const image = swatch.image || null;
          const hoverImage = swatch.hoverImage || image;
          const hasColorImage = swatch.hasColorImage !== false && Boolean(image?.url);
          const isActive = selectedColor && String(color).toLowerCase() === selectedColor;

          return `
            <button
              class="product-card__swatch${isActive ? ' product-card__swatch--active' : ''}"
              type="button"
              data-product-card-swatch
              data-product-card-url="${escapeHtml(getSearchProductUrl(root, swatch.url || product.url, product.handle))}"
              data-product-card-variant-id="${escapeHtml(variantId)}"
              data-product-card-color-value="${escapeHtml(color)}"
              data-swatch-tooltip="${escapeHtml(color)}"
              ${hasColorImage ? '' : 'data-product-card-image-missing="true"'}
              ${hasColorImage ? `
                data-image-src="${escapeHtml(withImageWidth(image.url, 540))}"
                data-image-srcset="${escapeHtml(buildSrcset(image.url))}"
                data-image-alt="${escapeHtml(image.altText || product.title)}"
                data-hover-image-src="${escapeHtml(withImageWidth(hoverImage?.url || image.url, 540))}"
                data-hover-image-srcset="${escapeHtml(buildSrcset(hoverImage?.url || image.url))}"
                data-hover-image-alt="${escapeHtml(hoverImage?.altText || product.title)}"
              ` : ''}
              aria-label="${escapeHtml(`${product.title} - ${color}`)}"
              aria-pressed="${isActive ? 'true' : 'false'}"
              ${swatchIndex >= visibleSwatchLimit ? 'hidden' : ''}
            >
              <span class="product-card__swatch-visual" data-swatch-value="${escapeHtml(color)}" style="${escapeHtml(getSwatchStyle(color))}"></span>
            </button>
          `;
        }).join('')}
        ${swatches.length > visibleSwatchLimit ? `<button class="product-card__swatch-more" type="button" data-product-card-swatches-expand aria-expanded="false" aria-label="Show ${swatches.length - visibleSwatchLimit} more colors for ${escapeHtml(product.title)}">+${swatches.length - visibleSwatchLimit}</button>` : ''}
      </div>
    `;
  };

  const normalizeMediaImage = (image) => {
    if (!image?.url) return null;

    return {
      url: image.url,
      altText: image.altText || '',
      width: image.width || null,
      height: image.height || null
    };
  };

  const renderMediaJson = (product) => {
    return safeJson({
      truncated: Boolean(product?.mediaTruncated),
      images: (product?.images || []).map(normalizeMediaImage).filter(Boolean),
      groups: (product?.mediaGroups || []).map((group) => ({
        color: group.color || '',
        images: (group.images || []).map(normalizeMediaImage).filter(Boolean)
      })).filter((group) => group.images.length)
    });
  };

  const renderVariantsJson = (root, product) => {
    return safeJson((product?.variants || []).map((variant) => ({
      id: getNumericId(variant),
      available: isVariantAvailable(variant),
      hidden: Boolean(variant.hidden),
      options: getVariantOptions(variant),
      price: Math.round(moneyAmount(variant.price) * 100),
      compare_at_price: Math.round(moneyAmount(variant.compareAtPrice) * 100),
      url: getSearchProductUrl(root, variant.url || product.url || '', product.handle)
    })));
  };

  const renderMediaControls = (root, product) => {
    return `
      <div class="product-card__media-controls" data-product-card-carousel-controls hidden>
        <button
          class="product-card__media-control product-card__media-control--prev"
          type="button"
          aria-label="Previous image for ${escapeHtml(product.title)}"
          data-product-card-carousel-prev
        >
          ${getIcon(root, 'chevron-left')}
        </button>
        <button
          class="product-card__media-control product-card__media-control--next"
          type="button"
          aria-label="Next image for ${escapeHtml(product.title)}"
          data-product-card-carousel-next
        >
          ${getIcon(root, 'chevron-right')}
        </button>
      </div>
    `;
  };

  const renderProductCard = (root, product, index) => {
    const productId = product.numericId || getNumericId(product.id) || product.handle || index;
    const cardId = `WorkerProductCard-${productId}-${index}`;
    const productUrl = getSearchProductUrl(root, product.url || `/products/${product.handle}`, product.handle);
    const imageRatio = root.dataset.imageRatio || 'portrait';
    const featuredImage = product.featuredImage || product.image || null;
    const secondaryImage = root.dataset.showSecondaryImage === 'true' ? product.secondaryImage : null;
    const showVendor = root.dataset.showVendor === 'true' && product.vendor;

    return `
      <article
        class="product-card product-card--${escapeHtml(imageRatio)}"
        data-search-product-result
        data-search-result-type="product"
        data-search-result-position="${index}"
        data-product-id="${escapeHtml(productId)}"
        data-product-handle="${escapeHtml(product.handle || '')}"
        data-product-title="${escapeHtml(product.title || '')}"
        data-product-card-size-option-index="${Number(product.sizeOptionIndex ?? -1)}"
        data-product-card-color-option-index="${Number(product.colorOptionIndex ?? -1)}"
      >
        <div class="product-card__media-shell">
          <a
            class="product-card__media"
            href="${escapeHtml(productUrl)}"
            aria-labelledby="${escapeHtml(cardId)}-title"
            data-product-card-link
            data-search-result-link
          >
            ${featuredImage?.url ? renderCardImage(featuredImage, product.title, { eager: index <= 4 }) : '<div class="product-card__placeholder" aria-hidden="true"></div>'}
            ${secondaryImage?.url && secondaryImage.url !== featuredImage?.url ? renderCardImage(secondaryImage, product.title, { className: 'product-card__secondary-image' }) : ''}
          </a>

          ${renderMediaControls(root, product)}

          <div class="product-card__badges" aria-hidden="true">
            ${renderBadges(product)}
          </div>

          <div class="product-card__image-actions">
            ${renderQuickAdd(root, product)}
          </div>
        </div>

        <div class="product-card__content">
          ${showVendor ? `<p class="product-card__vendor">${escapeHtml(product.vendor)}</p>` : ''}
          <h3 class="product-card__title" id="${escapeHtml(cardId)}-title">
            <a href="${escapeHtml(productUrl)}" data-product-card-link data-search-result-link>${escapeHtml(product.title || 'Product')}</a>
          </h3>
          <div class="product-card__details">
            ${renderPrice(root, product)}
          </div>
          ${renderSelectedColor(product)}
          ${renderSwatches(root, product)}
        </div>

        <script type="application/json" data-product-card-variants>${renderVariantsJson(root, product)}</script>
        <script type="application/json" data-product-card-media>${renderMediaJson(product)}</script>
      </article>
    `;
  };

  const renderCollectionCard = (collection, index) => {
    const collectionId = collection.numericId || getNumericId(collection.id) || collection.handle || index;
    const image = collection.image || collection.featuredImage || null;
    const url = getCollectionUrl(collection);
    const count = Number(collection.productCount ?? collection.productsCount ?? collection.count ?? 0);

    return `
      <article
        class="search-collection-card"
        data-search-collection-result
        data-search-result-type="collection"
        data-search-result-position="${index}"
        data-collection-id="${escapeHtml(collectionId)}"
        data-collection-handle="${escapeHtml(collection.handle || '')}"
      >
        <a class="search-collection-card__link" href="${escapeHtml(url)}" data-search-result-link>
          <span class="search-collection-card__image" aria-hidden="${image?.url ? 'false' : 'true'}">
            ${image?.url ? `
              <img
                src="${escapeHtml(withImageWidth(image.url, 360))}"
                srcset="${escapeHtml(buildSrcset(image.url))}"
                sizes="(min-width: 750px) 260px, 42vw"
                width="${image.width || 360}"
                height="${image.height || 480}"
                alt="${escapeHtml(image.altText || collection.title || 'Collection')}"
                loading="lazy"
                decoding="async"
              >
            ` : ''}
          </span>
          <span class="search-collection-card__content">
            <span class="search-collection-card__title">${escapeHtml(collection.title || collection.handle || 'Collection')}</span>
            ${count > 0 ? `<span class="search-collection-card__meta">${count} styles</span>` : ''}
          </span>
        </a>
      </article>
    `;
  };

  const createProductFragment = (root, products, startIndex = 0) => {
    const template = document.createElement('template');

    template.innerHTML = products.map((product, index) => renderProductCard(root, product, startIndex + index + 1)).join('');

    return template.content;
  };

  const createProductSkeletonFragment = (count, attributeName = 'data-search-loading-skeleton') => {
    const template = document.createElement('template');
    const cardCount = Math.max(1, Math.min(Number(count) || 4, 48));

    template.innerHTML = Array.from({ length: cardCount }, () => `
      <article class="product-card product-card--skeleton collection-worker-skeleton" ${attributeName} aria-hidden="true">
        <div class="product-card__media-shell">
          <div class="product-card__skeleton-media"></div>
          <div class="product-card__image-actions product-card__skeleton-action"></div>
        </div>
        <div class="product-card__content">
          <span class="product-card__skeleton-line product-card__skeleton-line--title"></span>
          <span class="product-card__skeleton-line product-card__skeleton-line--price"></span>
          <span class="product-card__skeleton-line product-card__skeleton-line--swatches"></span>
        </div>
      </article>
    `).join('');

    return template.content;
  };

  const getSearchSkeletonCount = (root) => {
    return Math.max(1, Math.min(Number(root?.dataset.searchProductLimit) || 24, 48));
  };

  const removeSearchLoadingSkeletons = (scope) => {
    scope?.querySelectorAll('[data-search-loading-skeleton]').forEach((skeleton) => {
      skeleton.remove();
    });

    if (scope instanceof Element && scope.matches('[data-search-products], [data-search-native-products]')) {
      scope.removeAttribute('aria-busy');
    }

    scope?.querySelectorAll('[data-search-products][aria-busy], [data-search-native-products][aria-busy]').forEach((grid) => {
      grid.removeAttribute('aria-busy');
    });
  };

  const appendSearchLoadingSkeletons = (root, grid, { replace = false } = {}) => {
    const productsSection = root.querySelector('[data-search-products-section]');
    const empty = root.querySelector('[data-search-empty]');

    if (!grid) return;

    if (replace) grid.replaceChildren();
    if (productsSection) productsSection.hidden = false;
    if (empty) empty.hidden = true;

    removeSearchLoadingSkeletons(grid);
    grid.setAttribute('aria-busy', 'true');
    grid.appendChild(createProductSkeletonFragment(getSearchSkeletonCount(root)));
  };

  const createCollectionFragment = (collections) => {
    const template = document.createElement('template');

    template.innerHTML = collections.map((collection, index) => renderCollectionCard(collection, index + 1)).join('');

    return template.content;
  };

  const getFallbackTrendingTerms = (root) => {
    const script = root.querySelector('[data-search-trending-fallbacks]');

    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent).map((term) => String(term || '').trim()).filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  const normalizeTrendingTerms = (payload, fallbackTerms) => {
    const source = payload?.trendingSearches
      || payload?.trending
      || payload?.searches
      || payload?.terms
      || [];
    const terms = source.map((item) => {
      return typeof item === 'string' ? item : item?.term || item?.query || item?.value || '';
    }).map((term) => String(term || '').trim()).filter(Boolean);

    return terms.length ? terms : fallbackTerms;
  };

  const normalizeProducts = (payload) => {
    const products = payload?.products
      || payload?.results?.products
      || payload?.data?.products
      || [];

    return products.map(normalizeSearchProduct).filter(Boolean);
  };

  const normalizeCollections = (payload) => {
    return payload?.collections
      || payload?.results?.collections
      || payload?.data?.collections
      || [];
  };

  const getTotalResults = (payload, products, collections) => {
    const explicitTotal = Number(payload?.productTotalResults ?? payload?.results?.productTotalResults ?? payload?.totalResults ?? payload?.total ?? payload?.results?.totalResults);

    return Number.isFinite(explicitTotal) ? explicitTotal : products.length + collections.length;
  };

  const renderTrendingTerms = (root, terms) => {
    const list = root.querySelector('[data-search-trending-list]');

    if (!list) return;

    const uniqueTerms = Array.from(new Set(terms.map((term) => String(term || '').trim()).filter(Boolean)));

    list.innerHTML = uniqueTerms.map((term) => {
      return `
        <a class="search-trending-list__item" href="/search?q=${encodeURIComponent(term)}" data-search-trending-link>
          ${escapeHtml(term)}
        </a>
      `;
    }).join('');
  };

  const setStatus = (root, message = '') => {
    const status = root.querySelector('[data-search-status]');

    if (status) status.textContent = message;
  };

  const setInfiniteStatus = (root, message = '') => {
    const status = root.querySelector('[data-search-infinite-status]');

    if (status) status.textContent = message;
  };

  const setNativeInfiniteStatus = (root, message = '') => {
    const status = root.querySelector('[data-search-native-infinite-status]');

    if (status) status.textContent = message;
  };

  const getNativeInfiniteNextLink = (root) => {
    return root.querySelector('[data-search-native-pagination] a[rel="next"]');
  };

  const isInfiniteScrollTriggerNearViewport = (trigger) => {
    if (!(trigger instanceof Element)) return false;

    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0;

    return rect.top <= viewportHeight + infiniteScrollPreloadMargin && rect.bottom >= -infiniteScrollPreloadMargin;
  };

  const initNativeInfiniteScroll = (root) => {
    if (root.dataset.searchNativeInfiniteInitialized === 'true') return true;
    if (!('IntersectionObserver' in window) || !('DOMParser' in window)) return false;

    const grid = root.querySelector('[data-search-native-products]');
    const pagination = root.querySelector('[data-search-native-pagination]');
    const trigger = root.querySelector('[data-search-native-infinite-trigger]');
    const sectionId = root.dataset.sectionId || '';
    let nextLink = getNativeInfiniteNextLink(root);
    let isLoading = false;
    let observer = null;
    let queuedVisibleLoad = false;

    if (!grid || !pagination || !trigger || !nextLink) return false;

    const showFallbackPagination = (message) => {
      root.classList.remove('search-template--native-infinite-enabled');
      root.classList.remove('search-template--loading-more');
      root.classList.add('search-template--native-infinite-error');
      removeSearchLoadingSkeletons(grid);
      grid.removeAttribute('aria-busy');
      setNativeInfiniteStatus(root, message);
      observer?.disconnect();
    };

    const getNextRoot = (documentFragment) => {
      const nextRoots = Array.from(documentFragment.querySelectorAll('[data-worker-search]'));

      return nextRoots.find((candidateRoot) => sectionId && candidateRoot.dataset.sectionId === sectionId) || nextRoots[0] || null;
    };

    const queueVisibleNativeLoad = () => {
      if (!nextLink || isLoading || queuedVisibleLoad) return;

      queuedVisibleLoad = true;
      window.requestAnimationFrame(() => {
        queuedVisibleLoad = false;

        if (nextLink && !isLoading && isInfiniteScrollTriggerNearViewport(trigger)) {
          loadNextNativePage();
        }
      });
    };

    const loadNextNativePage = async () => {
      if (isLoading || !nextLink) return;

      const nextUrl = nextLink.href;

      isLoading = true;
      root.classList.add('search-template--loading-more');
      grid.setAttribute('aria-busy', 'true');
      appendSearchLoadingSkeletons(root, grid);
      setNativeInfiniteStatus(root, 'Loading more styles...');

      try {
        const response = await fetch(nextUrl, {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Unable to load ${nextUrl}`);
        }

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = getNextRoot(parsedDocument);
        const nextGrid = nextRoot?.querySelector('[data-search-native-products]');
        const nextPagination = nextRoot?.querySelector('[data-search-native-pagination]');
        const nextItems = Array.from(nextGrid?.children || []).filter((child) => {
          return child instanceof HTMLElement && !child.classList.contains('empty-state');
        });

        if (!nextRoot || !nextGrid || !nextItems.length) {
          throw new Error(`No search results found in ${nextUrl}`);
        }

        const fragment = document.createDocumentFragment();

        nextItems.forEach((item) => {
          item.querySelectorAll('img[loading="eager"]').forEach((image) => {
            image.loading = 'lazy';
            image.removeAttribute('fetchpriority');
          });
          fragment.appendChild(item);
        });

        removeSearchLoadingSkeletons(grid);
        grid.appendChild(fragment);
        pagination.innerHTML = nextPagination?.innerHTML || '';
        nextLink = getNativeInfiniteNextLink(root);

        if (nextLink) {
          setNativeInfiniteStatus(root, '');
        } else {
          root.classList.add('search-template--native-infinite-complete');
          setNativeInfiniteStatus(root, 'All styles loaded.');
          observer?.disconnect();
        }
      } catch (error) {
        nextLink = null;
        showFallbackPagination('More styles could not load automatically. Use pagination to continue.');
      } finally {
        isLoading = false;
        root.classList.remove('search-template--loading-more');
        removeSearchLoadingSkeletons(grid);
        grid.removeAttribute('aria-busy');
        queueVisibleNativeLoad();
      }
    };

    observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadNextNativePage();
      }
    }, {
      rootMargin: infiniteScrollRootMargin
    });

    root.dataset.searchNativeInfiniteInitialized = 'true';
    root.classList.add('search-template--native-infinite-enabled');
    observer.observe(trigger);
    queueVisibleNativeLoad();

    return true;
  };

  const setTrendingVisible = (root, visible) => {
    const section = root.querySelector('[data-search-trending-section]');

    if (section) section.hidden = !visible;
  };

  const showNativeResults = (root) => {
    const nativeResults = root.querySelector('[data-search-native-results]');
    const workerResults = root.querySelector('[data-search-worker-results]');

    removeSearchLoadingSkeletons(root);
    if (nativeResults) nativeResults.hidden = false;
    if (workerResults) workerResults.hidden = true;
    root.classList.remove('search-template--worker-ready');
    root.classList.add('search-template--fallback');
    initNativeInfiniteScroll(root);
  };

  const showWorkerLoading = (root) => {
    const nativeResults = root.querySelector('[data-search-native-results]');
    const workerResults = root.querySelector('[data-search-worker-results]');

    if (nativeResults) nativeResults.hidden = true;
    if (workerResults) workerResults.hidden = false;
    root.classList.remove('search-template--worker-ready');
    root.classList.remove('search-template--fallback');
  };

  const showWorkerResults = (root) => {
    const nativeResults = root.querySelector('[data-search-native-results]');
    const workerResults = root.querySelector('[data-search-worker-results]');

    if (nativeResults) nativeResults.hidden = true;
    if (workerResults) workerResults.hidden = false;
    root.classList.add('search-template--worker-ready');
    root.classList.remove('search-template--fallback');
  };

  const updateUrl = (query) => {
    const nextUrl = new URL(window.location.href);

    if (query) {
      nextUrl.searchParams.set('q', query);
    } else {
      nextUrl.searchParams.delete('q');
    }

    window.history.pushState({ searchQuery: query }, '', nextUrl);
  };

  const requestTrending = async (root, { signal } = {}) => {
    const endpoint = normalizeEndpoint(root.dataset.searchWorkerEndpoint);
    const response = await fetch(`${endpoint}/search/trending?window=${encodeURIComponent(trendingWindow)}&limit=8`, {
      headers: { Accept: 'application/json' },
      signal
    });

    if (!response.ok) throw new Error('search-trending-unavailable');

    return response.json();
  };

  const requestSearch = async (root, query, { cursor = null, signal, requestId, resultSetId = '' } = {}) => {
    const endpoint = normalizeEndpoint(root.dataset.searchWorkerEndpoint);
    const productLimit = Number(root.dataset.searchProductLimit || 24);
    const body = {
      query,
      normalizedQuery: query.toLowerCase(),
      productLimit,
      collectionLimit: 0,
      pageSize: productLimit,
      include: ['products'],
      source: 'search-page',
      analytics: {
        requestId,
        sessionId: getWorkerSessionId(),
        resultSetId: resultSetId || requestId
      }
    };

    if (cursor) body.cursor = cursor;

    const response = await fetch(`${endpoint}/search`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body),
      signal
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload || payload?.fallback?.recommended) {
      throw new Error(payload?.fallback?.reason || 'search-worker-unavailable');
    }

    return payload;
  };

  const renderSearchPayload = (root, query, payload, requestId, state, { replace = true } = {}) => {
    const products = normalizeProducts(payload);
    const collections = [];
    const totalResults = getTotalResults(payload, products, collections);
    const productsSection = root.querySelector('[data-search-products-section]');
    const productsGrid = root.querySelector('[data-search-products]');
    const empty = root.querySelector('[data-search-empty]');
    const summary = root.querySelector('[data-search-summary]');
    const resultSetId = replace
      ? requestId
      : state.currentResultSetId || requestId;

    if (replace && summary) {
      summary.innerHTML = `
        <p class="collection-toolbar__count search-template__count">
          ${totalResults} results for "${escapeHtml(query)}"
        </p>
      `;
    }

    if (productsGrid) {
      if (replace) {
        productsGrid.replaceChildren();
        state.renderedProductCount = 0;
      } else {
        removeSearchLoadingSkeletons(productsGrid);
      }

      productsGrid.appendChild(createProductFragment(root, products, state.renderedProductCount));
      state.renderedProductCount += products.length;
    }

    state.nextCursor = payload?.pageInfo?.nextCursor || payload?.nextCursor || null;
    state.currentResultSetId = resultSetId;
    root.dataset.searchResultSetId = resultSetId;

    if (productsSection) productsSection.hidden = state.renderedProductCount === 0;
    if (empty) empty.hidden = state.renderedProductCount > 0;

    showWorkerResults(root);
    setTrendingVisible(root, false);
    setStatus(root, state.renderedProductCount ? '' : `No results found for ${query}.`);
    setInfiniteStatus(root, state.nextCursor ? '' : (state.renderedProductCount > 0 ? 'All styles loaded.' : ''));

  };

  const initRoot = (root) => {
    const enabled = root.dataset.searchWorkerEnabled === 'true';
    const workerPricingCompatible = window.WobWorkerMarket?.isPricingCompatible?.() !== false;
    const endpoint = normalizeEndpoint(root.dataset.searchWorkerEndpoint);
    const form = root.querySelector('[data-search-form]');
    const input = root.querySelector('[data-search-input]');
    const trigger = root.querySelector('[data-search-infinite-trigger]');
    const fallbackTerms = getFallbackTrendingTerms(root);
    const state = {
      abortController: null,
      debounceTimer: null,
      currentQuery: '',
      currentResultSetId: '',
      isLoadingMore: false,
      lastRenderedQuery: '',
      nextCursor: null,
      observer: null,
      queuedVisibleLoad: false,
      renderedProductCount: 0
    };

    if (!enabled || !workerPricingCompatible || !endpoint) {
      showNativeResults(root);
      return;
    }

    const getCurrentQuery = () => {
      return (input?.value || root.dataset.searchQuery || new URLSearchParams(window.location.search).get('q') || '').trim();
    };

    const abortPending = () => {
      state.abortController?.abort();
      state.abortController = null;
    };

    const disconnectInfiniteScroll = () => {
      state.observer?.disconnect();
      state.observer = null;
      state.nextCursor = null;
      state.queuedVisibleLoad = false;
      setInfiniteStatus(root, '');
    };

    const queueVisibleWorkerLoad = () => {
      if (!state.nextCursor || state.isLoadingMore || state.queuedVisibleLoad || !trigger) return;

      state.queuedVisibleLoad = true;
      window.requestAnimationFrame(() => {
        state.queuedVisibleLoad = false;

        if (state.nextCursor && !state.isLoadingMore && isInfiniteScrollTriggerNearViewport(trigger)) {
          loadNextSearchPage();
        }
      });
    };

    const observeInfiniteScroll = () => {
      if (!trigger || !('IntersectionObserver' in window) || !state.nextCursor) return;

      state.observer?.disconnect();
      state.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting) && state.nextCursor) {
          loadNextSearchPage();
        }
      }, {
        rootMargin: infiniteScrollRootMargin
      });
      state.observer.observe(trigger);
      queueVisibleWorkerLoad();
    };

    const loadNextSearchPage = async () => {
      if (!state.nextCursor || state.isLoadingMore || !state.currentQuery) return;

      const requestId = getRequestId();
      const cursor = state.nextCursor;
      const productsGrid = root.querySelector('[data-search-products]');

      state.isLoadingMore = true;
      root.classList.add('search-template--loading-more');
      appendSearchLoadingSkeletons(root, productsGrid);
      setInfiniteStatus(root, 'Loading more styles...');

      try {
        const payload = await requestSearch(root, state.currentQuery, {
          cursor,
          requestId,
          resultSetId: state.currentResultSetId
        });

        renderSearchPayload(root, state.currentQuery, payload, requestId, state, { replace: false });

        if (!state.nextCursor) {
          state.observer?.disconnect();
          state.observer = null;
        }
      } catch (error) {
        removeSearchLoadingSkeletons(productsGrid);
        state.nextCursor = null;
        state.observer?.disconnect();
        state.observer = null;
        setInfiniteStatus(root, 'More styles could not load automatically. Submit the search to use native results.');
      } finally {
        state.isLoadingMore = false;
        root.classList.remove('search-template--loading-more');
        removeSearchLoadingSkeletons(productsGrid);
        queueVisibleWorkerLoad();
      }
    };

    const loadTrending = async () => {
      renderTrendingTerms(root, fallbackTerms);

      try {
        const controller = new AbortController();
        const payload = await requestTrending(root, { signal: controller.signal });
        const terms = normalizeTrendingTerms(payload, fallbackTerms);

        renderTrendingTerms(root, terms);
      } catch (error) {
        renderTrendingTerms(root, fallbackTerms);
      }
    };

    const runSearch = async (query, { reason = 'type', shouldUpdateUrl = false, fallbackToNative = false } = {}) => {
      const normalizedQuery = String(query || '').trim();

      if (normalizedQuery.length < minimumQueryLength) {
        abortPending();
        disconnectInfiniteScroll();
        setStatus(root, normalizedQuery ? 'Keep typing to search.' : '');
        setTrendingVisible(root, false);
        showNativeResults(root);
        return;
      }

      abortPending();
      disconnectInfiniteScroll();

      const requestId = getRequestId();
      const controller = new AbortController();
      state.abortController = controller;
      state.currentQuery = normalizedQuery;
      state.renderedProductCount = 0;
      root.classList.add('search-template--loading');
      setStatus(root, 'Searching...');
      showWorkerLoading(root);
      appendSearchLoadingSkeletons(root, root.querySelector('[data-search-products]'), { replace: true });

      try {
        const payload = await requestSearch(root, normalizedQuery, {
          requestId,
          signal: controller.signal
        });

        if (controller.signal.aborted) return;

        state.lastRenderedQuery = normalizedQuery;
        renderSearchPayload(root, normalizedQuery, payload, requestId, state, { replace: true });
        observeInfiniteScroll();

        if (shouldUpdateUrl) updateUrl(normalizedQuery);
      } catch (error) {
        if (controller.signal.aborted) return;

        removeSearchLoadingSkeletons(root);
        setStatus(root, '');
        disconnectInfiniteScroll();
        showNativeResults(root);
        setTrendingVisible(root, false);

        if (fallbackToNative && form) {
          form.submit();
        }
      } finally {
        if (state.abortController === controller) {
          state.abortController = null;
        }

        root.classList.remove('search-template--loading');
      }
    };

    if (form && input) {
      form.addEventListener('submit', (event) => {
        const query = input.value.trim();

        if (query.length < minimumQueryLength) return;

        event.preventDefault();
        window.clearTimeout(state.debounceTimer);
        runSearch(query, {
          fallbackToNative: true,
          reason: 'submit',
          shouldUpdateUrl: true
        });
      });

      input.addEventListener('input', () => {
        const query = input.value.trim();
        const debounceMs = Number(root.dataset.searchDebounceMs || 300);

        window.clearTimeout(state.debounceTimer);

        if (query.length === 0) {
          abortPending();
          disconnectInfiniteScroll();
          setStatus(root, '');
          setTrendingVisible(root, false);
          showNativeResults(root);
          return;
        }

        if (query.length < minimumQueryLength) {
          abortPending();
          disconnectInfiniteScroll();
          setStatus(root, 'Keep typing to search.');
          return;
        }

        state.debounceTimer = window.setTimeout(() => {
          runSearch(query, { reason: 'type' });
        }, debounceMs);
      });
    }

    window.addEventListener('popstate', () => {
      const query = new URLSearchParams(window.location.search).get('q') || '';

      if (input) input.value = query;
      root.dataset.searchQuery = query;
      runSearch(query, { reason: 'history' });
    });

    if (root.querySelector('[data-search-trending-section]')) {
      loadTrending();
    }

    const initialQuery = getCurrentQuery();

    if (initialQuery.length >= minimumQueryLength) {
      runSearch(initialQuery, { reason: 'initial' });
    } else {
      setTrendingVisible(root, false);
      showNativeResults(root);
    }
  };

  roots.forEach(initRoot);
})();
